import React, { useState, useEffect, useRef } from 'react';
import './PromoCode.css';
import { toast } from 'react-toastify';
import { Check, ChevronRight, Copy, Plus, TicketPercent } from 'lucide-react';
import {
    Badge,
    Button,
    Card,
    Checkbox,
    EmptyState,
    Input,
    Modal,
    PageHeader,
    SearchInput,
    SegmentedControl,
    Select,
    Spinner,
    TableSkeleton,
} from '../../Components/ui';
import './PromoCodePage.css';

// ── Faqat ko'rsatish uchun yordamchilar (API qiymatlari o'zgarmaydi) ──
const TYPE_META = {
    access: { label: 'Kirish', variant: 'info' },
    discount: { label: 'Chegirma', variant: 'brand' },
};

const STATUS_META = {
    active: { label: 'Faol', variant: 'success' },
    expired: { label: "Muddati o'tgan", variant: 'warning' },
    upcoming: { label: 'Boshlanmagan', variant: 'info' },
    inactive: { label: 'Nofaol', variant: 'neutral' },
};

// "YYYY-MM-DD..." → "YYYY-MM-DD"; bo'sh yoki 1970-01-01 → null (belgilanmagan).
const toDay = (value) => {
    if (!value) return null;
    const day = String(value).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || day === '1970-01-01') return null;
    return day;
};

const formatDay = (day) => day.split('-').reverse().join('.');

const getTodayKey = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

// Hisoblangan holat: nofaol → muddati o'tgan → boshlanmagan → faol.
const getPromoStatus = (promo, today) => {
    if (!promo.is_active) return 'inactive';
    const to = toDay(promo.valid_to);
    const from = toDay(promo.valid_from);
    if (to && to < today) return 'expired';
    if (from && from > today) return 'upcoming';
    return 'active';
};

function PromoPeriod({ from, to }) {
    const start = toDay(from);
    const end = toDay(to);
    if (!start && !end) return <span className="ui-promo__muted">Belgilanmagan</span>;
    return (
        <span className="ui-promo__period">
            {start ? formatDay(start) : <span className="ui-promo__muted">Belgilanmagan</span>}
            {' — '}
            {end ? formatDay(end) : <span className="ui-promo__muted">Belgilanmagan</span>}
        </span>
    );
}

function PromoCourses({ promo }) {
    if (promo.all_course) return <span className="ui-promo__chip ui-promo__chip--all">Barcha kurslar</span>;
    const list = Array.isArray(promo.courses) ? promo.courses : [];
    if (list.length === 0) return <span className="ui-promo__muted">—</span>;
    const rest = list.slice(2);
    return (
        <div className="ui-promo__chips">
            {list.slice(0, 2).map((course) => (
                <span key={course.id} className="ui-promo__chip" title={course.title}>
                    {course.title}
                </span>
            ))}
            {rest.length > 0 && (
                <span className="ui-promo__chip ui-promo__chip--more" title={rest.map((course) => course.title).join('\n')}>
                    +{rest.length}
                </span>
            )}
        </div>
    );
}

const PromoList = () => {
    const [promos, setPromos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showCodesModal, setShowCodesModal] = useState(false);
    const [selectedPromoCodes, setSelectedPromoCodes] = useState([]);
    const [codesLoading, setCodesLoading] = useState(false);
    const [courses, setCourses] = useState([]);
    const [formData, setFormData] = useState({
        promo_type: 'access',
        all_course: false,
        discount_percentage: 0,
        is_active: true,
        valid_from: '',
        valid_to: '',
        codes_count: 1,
        courses: [],
        usage_limit: 1,
        quantity: 1
    });
    const [formLoading, setFormLoading] = useState(false);

    // Faqat ko'rinish uchun UI state'lar (API'ga ta'sir qilmaydi)
    const [statusFilter, setStatusFilter] = useState('all');
    const [copiedCodeId, setCopiedCodeId] = useState(null);
    const [courseSearch, setCourseSearch] = useState('');
    const copiedTimerRef = useRef(null);

    useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

    useEffect(() => {
        fetchPromos();
        fetchCourses();
    }, []);

    const fetchCourses = async () => {
        try {
            const token = localStorage.getItem('admin_token');
            const response = await fetch('https://iifabackend.soof.uz/courses', {
                method: 'GET',
                headers: {
                    'accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setCourses(data);
            }
        } catch (err) {
            console.error('Kurslarni yuklashda xatolik:', err);
        }
    };

    const fetchPromos = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('admin_token');

            const response = await fetch('https://iifabackend.soof.uz/promo/list', {
                method: 'GET',
                headers: {
                    'accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Xatolik yuz berdi');
            }

            const data = await response.json();
            setPromos(data);
            setError(null);
        } catch (err) {
            setError(err.message || 'Xatolik yuz berdi');
        } finally {
            setLoading(false);
        }
    };

    const fetchPromoCodes = async (promoId) => {
        try {
            setCodesLoading(true);
            const token = localStorage.getItem('admin_token');

            const response = await fetch(`https://iifabackend.soof.uz/promo/${promoId}/codes`, {
                method: 'GET',
                headers: {
                    'accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Kodlarni yuklashda xatolik');
            }

            const data = await response.json();
            setSelectedPromoCodes(data);
            setShowCodesModal(true);
        } catch (err) {
            toast.error('Xatolik: ' + err.message);
        } finally {
            setCodesLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleCourseSelect = (courseId) => {
        setFormData(prev => {
            const isSelected = prev.courses.includes(courseId);
            return {
                ...prev,
                courses: isSelected
                    ? prev.courses.filter(id => id !== courseId)
                    : [...prev.courses, courseId]
            };
        });
    };

    const handleSubmit = async () => {
        setFormLoading(true);

        try {
            const token = localStorage.getItem('admin_token');
            const submitData = {
                promo_type: formData.promo_type,
                all_course: formData.all_course,
                discount_percentage: parseInt(formData.discount_percentage),
                is_active: formData.is_active,
                valid_from: formData.valid_from,
                valid_to: formData.valid_to,
                codes_count: parseInt(formData.codes_count),
                courses: formData.all_course ? null : formData.courses,
                usage_limit: parseInt(formData.usage_limit),
                quantity: parseInt(formData.quantity)
            };

            const response = await fetch('https://iifabackend.soof.uz/promo/create', {
                method: 'POST',
                headers: {
                    'accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(submitData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Xatolik yuz berdi');
            }

            const result = await response.json();

            setFormData({
                promo_type: 'access',
                all_course: false,
                discount_percentage: 0,
                is_active: true,
                valid_from: '',
                valid_to: '',
                codes_count: 1,
                courses: [],
                usage_limit: 1,
                quantity: 1
            });

            setShowAddModal(false);
            fetchPromos();
        } catch (err) {
            toast.error('Xatolik: ' + err.message);
        } finally {
            setFormLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Kod nusxalandi!');
    };

    // "Nusxalandi" belgisi 2 soniya ko'rinadi (nusxalash mantiqi — copyToClipboard).
    const markCopied = (codeId) => {
        setCopiedCodeId(codeId);
        clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = setTimeout(() => setCopiedCodeId(null), 2000);
    };

    // Barcha kodlar — har qatorga bittadan (faqat clipboard).
    const copyAllCodes = () => {
        navigator.clipboard.writeText(selectedPromoCodes.map((item) => item.code).join('\n'));
        toast.success('Barcha kodlar nusxalandi!');
    };

    const today = getTodayKey();
    const sortedPromos = [...promos].sort((a, b) => b.id - a.id);
    const promoStatuses = Object.fromEntries(sortedPromos.map((promo) => [promo.id, getPromoStatus(promo, today)]));
    const countBy = (status) => sortedPromos.filter((promo) => promoStatuses[promo.id] === status).length;
    const visiblePromos = statusFilter === 'all'
        ? sortedPromos
        : sortedPromos.filter((promo) => promoStatuses[promo.id] === statusFilter);

    const visibleCourses = courseSearch.trim()
        ? courses.filter((course) => String(course.title || '').toLowerCase().includes(courseSearch.trim().toLowerCase()))
        : courses;

    const header = (
        <PageHeader
            title="Promo kodlar"
            subtitle="Kirish va chegirma kodlari guruhlari"
            actions={
                <>
                    {codesLoading && (
                        <span className="ui-promo__codes-loading">
                            <Spinner size="sm" label="Kodlar yuklanmoqda" /> Kodlar yuklanmoqda…
                        </span>
                    )}
                    <Button leftIcon={<Plus />} onClick={() => setShowAddModal(true)}>
                        Promo kod yaratish
                    </Button>
                </>
            }
        />
    );

    let content;
    if (loading) {
        content = <TableSkeleton rows={8} cols={6} />;
    } else if (error) {
        content = (
            <Card padding="none">
                <EmptyState
                    title="Xatolik"
                    description={error}
                    action={<Button variant="secondary" size="sm" onClick={fetchPromos}>Qayta urinish</Button>}
                />
            </Card>
        );
    } else if (promos.length === 0) {
        content = (
            <Card padding="none">
                <EmptyState
                    icon={<TicketPercent />}
                    title="Promo kodlar hali yo'q"
                    action={<Button size="sm" leftIcon={<Plus />} onClick={() => setShowAddModal(true)}>Promo kod yaratish</Button>}
                />
            </Card>
        );
    } else {
        content = (
            <>
                <SegmentedControl
                    aria-label="Holat bo'yicha filtr"
                    className="ui-promo__filter"
                    options={[
                        { value: 'all', label: `Hammasi (${sortedPromos.length})` },
                        { value: 'active', label: `Faol (${countBy('active')})` },
                        { value: 'expired', label: `Muddati o'tgan (${countBy('expired')})` },
                        { value: 'inactive', label: `Nofaol (${countBy('inactive')})` },
                    ]}
                    value={statusFilter}
                    onChange={setStatusFilter}
                />

                {visiblePromos.length === 0 ? (
                    <Card padding="none">
                        <EmptyState title="Bu holatdagi promo kodlar yo'q" />
                    </Card>
                ) : (
                    <div className="ui-table-wrap">
                        <table className="ui-table ui-promo__table">
                            <thead>
                                <tr>
                                    <th>Turi</th>
                                    <th>Holat</th>
                                    <th className="ui-table-cell--numeric">Kodlar soni</th>
                                    <th>Amal qilish muddati</th>
                                    <th>Kurslar</th>
                                    <th aria-label="Amallar" />
                                </tr>
                            </thead>
                            <tbody>
                                {visiblePromos.map((promo) => {
                                    const type = TYPE_META[promo.promo_type];
                                    const status = STATUS_META[promoStatuses[promo.id]];
                                    return (
                                        <tr
                                            key={promo.id}
                                            className="ui-table-row--clickable"
                                            onClick={() => fetchPromoCodes(promo.id)}
                                        >
                                            <td>
                                                <div className="ui-promo__type">
                                                    <Badge variant={type ? type.variant : 'neutral'} size="sm">
                                                        {type ? type.label : promo.promo_type}
                                                    </Badge>
                                                    {promo.discount_percentage > 0 && (
                                                        <span className="ui-promo__discount">{promo.discount_percentage}%</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <Badge variant={status.variant} size="sm" dot>{status.label}</Badge>
                                            </td>
                                            <td className="ui-table-cell--numeric">{promo.codes_count}</td>
                                            <td>
                                                <PromoPeriod from={promo.valid_from} to={promo.valid_to} />
                                            </td>
                                            <td>
                                                <PromoCourses promo={promo} />
                                            </td>
                                            <td className="ui-promo__actions">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    rightIcon={<ChevronRight />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        fetchPromoCodes(promo.id);
                                                    }}
                                                >
                                                    Kodlar
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </>
        );
    }

    return (
        <div className="ui-promo">
            {header}
            {content}

            {/* Add Promo Modal */}
            <Modal
                open={showAddModal}
                onClose={() => setShowAddModal(false)}
                title="Promo kod yaratish"
                size="md"
                footer={
                    <>
                        <Button
                            variant="secondary"
                            onClick={() => setShowAddModal(false)}
                            disabled={formLoading}
                        >
                            Bekor qilish
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            loading={formLoading}
                        >
                            Saqlash
                        </Button>
                    </>
                }
            >
                <div className="ui-promo-form">
                    <div className="ui-promo-form__grid">
                        <Select
                            label="Promo turi"
                            name="promo_type"
                            value={formData.promo_type}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="access">Kirish</option>
                            <option value="discount">Chegirma</option>
                        </Select>

                        <Input
                            label="Kodlar soni"
                            type="number"
                            name="codes_count"
                            value={formData.codes_count}
                            onChange={handleInputChange}
                            min="1"
                            required
                        />

                        <Input
                            label="Boshlanish sanasi"
                            type="date"
                            name="valid_from"
                            value={formData.valid_from}
                            onChange={handleInputChange}
                            required
                        />

                        <Input
                            label="Tugash sanasi"
                            type="date"
                            name="valid_to"
                            value={formData.valid_to}
                            onChange={handleInputChange}
                            required
                        />

                        <Checkbox
                            label="Faol"
                            name="is_active"
                            checked={formData.is_active}
                            onChange={handleInputChange}
                        />

                        <Checkbox
                            label="Barcha kurslar uchun"
                            name="all_course"
                            checked={formData.all_course}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className={`ui-promo-form__courses ${formData.all_course ? 'ui-promo-form__courses--disabled' : ''}`}>
                        <div className="ui-promo-form__courses-head">
                            <span className="ui-promo-form__label">Kurslar</span>
                            <span className="ui-promo__muted">{formData.courses.length} ta tanlandi</span>
                        </div>
                        <SearchInput
                            placeholder="Kurs qidirish..."
                            value={courseSearch}
                            onChange={(e) => setCourseSearch(e.target.value)}
                            disabled={formData.all_course}
                        />
                        <div className="ui-promo-form__course-list">
                            {courses.length === 0 ? (
                                <p className="ui-promo__muted">Kurslar yuklanmoqda...</p>
                            ) : visibleCourses.length === 0 ? (
                                <p className="ui-promo__muted">Kurs topilmadi</p>
                            ) : (
                                visibleCourses.map(course => (
                                    <Checkbox
                                        key={course.id}
                                        label={course.title}
                                        checked={formData.courses.includes(course.id)}
                                        onChange={() => handleCourseSelect(course.id)}
                                        disabled={formData.all_course}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Codes Modal */}
            <Modal
                open={showCodesModal}
                onClose={() => setShowCodesModal(false)}
                title="Promo kodlar"
                subtitle={`${selectedPromoCodes.length} ta kod`}
                size="lg"
                closeOnOverlayClick
                footer={
                    <Button
                        variant="secondary"
                        leftIcon={<Copy />}
                        onClick={copyAllCodes}
                        disabled={codesLoading || selectedPromoCodes.length === 0}
                    >
                        Barcha kodlarni nusxalash
                    </Button>
                }
            >
                {codesLoading ? (
                    <TableSkeleton rows={5} cols={5} />
                ) : selectedPromoCodes.length === 0 ? (
                    <EmptyState title="Kodlar topilmadi" />
                ) : (
                    <div className="ui-table-wrap">
                        <table className="ui-table ui-promo__codes-table">
                            <thead>
                                <tr>
                                    <th>Kod</th>
                                    <th className="ui-table-cell--numeric">Limit</th>
                                    <th className="ui-table-cell--numeric">Ishlatilgan</th>
                                    <th>Holat</th>
                                    <th aria-label="Nusxalash" />
                                </tr>
                            </thead>
                            <tbody>
                                {selectedPromoCodes.map((code) => (
                                    <tr key={code.id}>
                                        <td><span className="ui-promo__code">{code.code}</span></td>
                                        <td className="ui-table-cell--numeric">{code.usage_limit}</td>
                                        <td className="ui-table-cell--numeric">{code.used_count} / {code.usage_limit}</td>
                                        <td>
                                            {code.used_count >= code.usage_limit ? (
                                                <Badge variant="neutral" size="sm">Ishlatilgan</Badge>
                                            ) : (
                                                <Badge variant="success" size="sm">Bo'sh</Badge>
                                            )}
                                        </td>
                                        <td className="ui-promo__actions">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                iconOnly
                                                aria-label="Nusxalash"
                                                title="Nusxalash"
                                                onClick={() => {
                                                    copyToClipboard(code.code);
                                                    markCopied(code.id);
                                                }}
                                            >
                                                {copiedCodeId === code.id ? <Check className="ui-promo__copied" /> : <Copy />}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default PromoList;